import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { SchoolYearDTO } from 'src/app/common/dtos/school.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { deepCopy } from 'src/app/common/utilities/copy.helpers';
import { select } from 'src/app/common/utilities/ngxs-utils';
import { checkIfE2L } from 'src/app/common/utilities/role-helpers';
import { CreateEditPhaseModalComponent } from '../../shared/components/modals/create-edit-phase-modal/create-edit-phase-modal.component';
import { CreateEditPlanModalComponent } from '../../shared/components/modals/create-edit-plan-modal/create-edit-plan-modal.component';
import { CreateDeliverableModalComponent } from '../../shared/components/plans/create-deliverable-modal/create-deliverable-modal.component';
import { EditActionItemModalComponent } from '../../shared/components/plans/edit-action-item-modal/edit-action-item-modal.component';
import { EditDeliverableModalComponent } from '../../shared/components/plans/edit-deliverable-modal/edit-deliverable-modal.component';
import { PlansSortingService } from '../../shared/services/plans/plans-sorting.service';
import { PlansService } from '../../shared/services/plans/plans.service';
import {
  CURRENT_SCHOOL_YEAR,
  getSchoolYearListWithFutures,
} from '../../shared/services/school-year/school-year.utilities';
import {
  EditActionItem,
  EditDeliverable,
  EditPhase,
  FetchPlanDetails,
  ToggleDeliverableExpandedState,
  TogglePhaseExpandedState,
} from '../../shared/state/implementation-plan/implementation-plan.actions';
import {
  ActionItemStateModel,
  DeliverableStateModel,
  ImplementationPlanState,
  PhaseStateModel,
  PlanDetailsItemStateModel,
} from '../../shared/state/implementation-plan/implementation-plan.state';
import {
  ActionItemAPIResponse,
  DeliverableAPIResponse,
  PhaseAPIResponse,
  PlansAPIResponse,
  StatusType,
} from '../../shared/types/responses/plan.responses';
import { checkIfPlanEditor } from '../plans-page/plans-page-helpers';

@Component({
  selector: 'app-implementation-plan-details-page',
  templateUrl: './implementation-plan-details-page.component.html',
  styleUrl: './implementation-plan-details-page.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImplementationPlanDetailsPageComponent
  implements OnInit, OnDestroy
{
  @ViewChild('createEditPlanModal')
  createEditPlanModal: CreateEditPlanModalComponent;

  @ViewChild('createEditPhaseModal')
  createEditPhaseModal: CreateEditPhaseModalComponent;

  @ViewChild('createDeliverableParentModal')
  createDeliverableParentModal: CreateDeliverableModalComponent;

  @ViewChild('editDeliverableParentModal')
  editDeliverableParentModal: EditDeliverableModalComponent;

  @ViewChild('editActionItemModal')
  editActionItemModal: EditActionItemModalComponent;

  user: User;

  isE2L = false;

  planId: number;

  planDetails$: Observable<PlanDetailsItemStateModel> = select(
    ImplementationPlanState.getPlanDetails
  );

  planDetails: PlanDetailsItemStateModel;

  currentEditPlan: PlansAPIResponse | null = null;

  formattedStartDate = '';

  formattedEndDate = '';

  currentPhaseId: number;

  currentDeliverable: DeliverableAPIResponse;

  currentActionItem: ActionItemAPIResponse;

  subs: Subscription = new Subscription();

  planPermissions = [];

  planEditor = false;

  planSchoolYearList: SchoolYearDTO[] = [];

  selectedSchoolYear: SchoolYearDTO | null = null;

  actionItemsHiddenMessage: string;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private plansService: PlansService,
    private plansSortingService: PlansSortingService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    if (this.user) {
      this.isE2L = checkIfE2L(this.user);
    }
  }

  ngOnInit(): void {
    this.subs.add(
      this.route.params.subscribe((params) => {
        if (params['planId']) {
          this.planId = parseInt(params['planId']);
          this.store.dispatch(new FetchPlanDetails(this.planId));

          let modalOpenedFromUrl = false;

          this.planDetails$
            .pipe(takeUntil(this.destroy$))
            .subscribe((details: PlanDetailsItemStateModel) => {
              if (details) {
                const datesChanged =
                  !this.planDetails ||
                  details.start_date !== this.planDetails.start_date ||
                  details.end_date !== this.planDetails.end_date;

                this.planDetails = details;
                this.cdr.markForCheck();

                if (datesChanged && details.start_date && details.end_date) {
                  this.formattedStartDate = this.formatDate(details.start_date);
                  this.formattedEndDate = this.formatDate(details.end_date);
                  this.getPlanSchoolYears();
                }

                if (this.user) {
                  this.planEditor = checkIfPlanEditor(
                    this.user,
                    this.planDetails
                  );
                }

                // update current Deliverable for editing
                if (this.currentDeliverable && this.currentPhaseId) {
                  this.currentDeliverable = this.findDeliverable(
                    this.currentPhaseId,
                    this.currentDeliverable.id
                  );
                  // update current Action Item for editing
                  if (this.currentActionItem) {
                    this.currentActionItem = this.findActionItem(
                      this.currentPhaseId,
                      this.currentDeliverable.id,
                      this.currentActionItem.id
                    );
                  }
                }

                if (!modalOpenedFromUrl && this.planDetails.phase_displayname) {
                  modalOpenedFromUrl = true;
                  setTimeout(() => {
                    this.handleQueryParams();
                  });
                }

                this.actionItemsHiddenMessage = `There are additional ${details.actionitem_displayname}(s) that do not match the chosen filter`;
              }
            });
        }
      })
    );
  }

  handleQueryParams() {
    this.subs.add(
      this.route.queryParams.subscribe((queryParams) => {
        const deliverableId = queryParams['deliverable_id'];
        const actionItemId = queryParams['action_item_id'];

        if (this.planDetails?.phases) {
          if (deliverableId) {
            this.planDetails.phases.forEach((phase) => {
              phase.deliverables.forEach((deliverable) => {
                if (
                  deliverable.id === parseInt(deliverableId) &&
                  this.editDeliverableParentModal
                ) {
                  this.openEditDeliverableModal(deliverable.id, phase.id);
                }
              });
            });
          }

          if (actionItemId) {
            this.planDetails.phases.forEach((phase) => {
              phase.deliverables.forEach((deliverable) => {
                deliverable.actionItems.forEach((actionItem) => {
                  if (
                    actionItem.id === parseInt(actionItemId) &&
                    this.editActionItemModal
                  ) {
                    this.openEditActionItemModal(
                      actionItem.id,
                      deliverable.id,
                      phase.id
                    );
                  }
                });
              });
            });
          }
        }
      })
    );
  }

  findDeliverable(phaseId: number, delId: number): DeliverableAPIResponse {
    const phase = this.planDetails.phases.filter(
      (phaseItem) => phaseItem.id === phaseId
    );
    const del = phase[0].deliverables.filter((deliv) => deliv.id === delId);
    return del[0];
  }

  findActionItem(
    phaseId: number,
    delId: number,
    actionItemId: number
  ): ActionItemAPIResponse {
    const phase = this.planDetails.phases.filter(
      (phaseItem) => phaseItem.id === phaseId
    );
    const del = phase[0].deliverables.filter((deliv) => deliv.id === delId);
    const actionItem = del[0].actionItems.filter(
      (item) => item.id === actionItemId
    );
    return actionItem[0];
  }

  /* eslint-disable-next-line class-methods-use-this */
  formatDate(date: string) {
    // date comes in YYYY-MM-DD format
    const dates = date.split('-');
    return `${dates[1]}/${dates[2]}/${dates[0]}`;
  }

  /* eslint-disable-next-line class-methods-use-this */
  formatProgress(progress: number) {
    return Math.round(progress * 10000) / 100;
  }

  togglePhase(phaseId: number) {
    // Timeout so that collapse animation can finish before toggle state is updated
    setTimeout(() => {
      this.store.dispatch(new TogglePhaseExpandedState({ phaseId }));
    }, 500);
  }

  toggleDeliverable(deliverableId: number) {
    // Timeout so that collapse animation can finish before toggle state is updated
    setTimeout(() => {
      this.store.dispatch(
        new ToggleDeliverableExpandedState({ deliverableId })
      );
    }, 500);
  }

  // eslint-disable-next-line class-methods-use-this
  trackByPhaseId(_index: number, phase: PhaseStateModel): number {
    return phase.id;
  }

  // eslint-disable-next-line class-methods-use-this
  trackByDeliverableId(
    _index: number,
    deliverable: DeliverableStateModel
  ): number {
    return deliverable.id;
  }

  // eslint-disable-next-line class-methods-use-this
  trackByActionItemId(
    _index: number,
    actionItem: ActionItemStateModel
  ): number {
    return actionItem.id;
  }

  // eslint-disable-next-line class-methods-use-this
  trackByAssignmentUserId(
    _index: number,
    assignment: { user_id: number }
  ): number {
    return assignment.user_id;
  }

  openCreatePhaseModal() {
    this.createEditPhaseModal.isEditMode = false;
    this.createEditPhaseModal.modal.config.titleText = `Create ${this.planDetails?.phase_displayname}`;
    this.createEditPhaseModal.phaseToEdit = undefined;
    this.createEditPhaseModal.modal.open();
  }

  openEditPlanModal() {
    this.currentEditPlan = deepCopy(this.planDetails) as PlansAPIResponse;
    this.cdr.detectChanges();
    this.createEditPlanModal.createEditPlanModal.modalConfig.titleText =
      'Edit Implementation Plan';
    this.createEditPlanModal.createEditPlanModal.loadConfig();
    this.createEditPlanModal.createEditPlanModal.open();
  }

  openEditPhaseModal(phase: PhaseAPIResponse) {
    this.createEditPhaseModal.isEditMode = true;
    this.createEditPhaseModal.modal.config.titleText = `Edit ${this.planDetails?.phase_displayname}`;
    this.createEditPhaseModal.phaseToEdit = phase;
    this.createEditPhaseModal.modal.open();
  }

  onPhaseStatusChange(phaseId: number, status: StatusType) {
    this.store.dispatch(new EditPhase({ phase: { id: phaseId, status } }));
    this.subs.add(
      this.plansService.updatePhaseStatus(phaseId, status).subscribe((res) => {
        this.store.dispatch(
          new EditPhase({
            phase: {
              id: phaseId,
              status,
              ...(res?.item?.progress !== undefined && {
                progress: res.item.progress,
              }),
            },
          })
        );
      })
    );
  }

  onDeliverableStatusChange(deliverableId: number, status: StatusType) {
    this.store.dispatch(new EditDeliverable({ id: deliverableId, status }));
    this.subs.add(
      this.plansService
        .updateDeliverableStatus(deliverableId, status)
        .subscribe()
    );
  }

  onActionItemStatusChange(actionItemId: number, status: StatusType) {
    this.store.dispatch(new EditActionItem({ id: actionItemId, status }));
    this.subs.add(
      this.plansService.updateActionItemStatus(actionItemId, status).subscribe()
    );
  }

  openCreateDeliverableModal(phaseId: number) {
    this.createDeliverableParentModal.createDeliverableModal.config.titleText = `New ${this.planDetails?.deliverable_displayname}`;
    this.createDeliverableParentModal.phaseId = phaseId;
    this.createDeliverableParentModal.createDeliverableModal.open();
  }

  openEditDeliverableModal(deliverableId: number, phaseId: number) {
    this.currentDeliverable = this.findDeliverable(phaseId, deliverableId);
    this.currentPhaseId = phaseId;
    this.editDeliverableParentModal.editDeliverableModal.open();
  }

  openEditActionItemModal(
    actionItemId: number,
    deliverableId: number,
    phaseId: number
  ) {
    this.currentActionItem = this.findActionItem(
      phaseId,
      deliverableId,
      actionItemId
    );
    this.currentDeliverable = this.findDeliverable(phaseId, deliverableId);
    this.currentPhaseId = phaseId;
    this.editActionItemModal.openModal();
  }

  dropPhase(phase: PhaseStateModel[], event: CdkDragDrop<PhaseStateModel[]>) {
    const sortData = this.plansSortingService.calcSortChange(
      phase,
      event.previousIndex,
      event.currentIndex
    );
    moveItemInArray(phase, event.previousIndex, event.currentIndex);
    if (sortData) {
      this.subs.add(
        this.plansSortingService
          .sortPhase(sortData.id, sortData.direction, sortData.refId)
          .subscribe((response) => {
            if (response) {
              this.store.dispatch(
                new EditPhase({
                  phase: {
                    id: response.item.id,
                    sort_order: response.item.sort_order,
                  },
                })
              );
            }
          })
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any, class-methods-use-this
  dropDeliverable(
    deliverable: DeliverableStateModel[],
    event: CdkDragDrop<DeliverableStateModel[]>
  ) {
    const sortData = this.plansSortingService.calcSortChange(
      deliverable,
      event.previousIndex,
      event.currentIndex
    );
    moveItemInArray(deliverable, event.previousIndex, event.currentIndex);
    if (sortData) {
      this.subs.add(
        this.plansSortingService
          .sortDeliverable(sortData.id, sortData.direction, sortData.refId)
          .subscribe((response) => {
            if (response) {
              this.store.dispatch(
                new EditDeliverable({
                  id: response.item.id,
                  sort_order: response.item.sort_order,
                })
              );
            }
          })
      );
    }
  }

  dropActionItem(
    deliverable: ActionItemStateModel[],
    event: CdkDragDrop<ActionItemStateModel[]>
  ) {
    const sortData = this.plansSortingService.calcSortChange(
      deliverable,
      event.previousIndex,
      event.currentIndex
    );
    moveItemInArray(deliverable, event.previousIndex, event.currentIndex);
    if (sortData) {
      this.subs.add(
        this.plansSortingService
          .sortActionItem(sortData.id, sortData.direction, sortData.refId)
          .subscribe((response) => {
            if (response) {
              this.store.dispatch(
                new EditActionItem({
                  id: response.item.id,
                  sort_order: response.item.sort_order,
                })
              );
            }
          })
      );
    }
  }

  getPlanSchoolYears() {
    // Dates are stored in YYYY-MM-DD format directly from the store
    const [startYear, startMonth, startDay] = this.planDetails.start_date
      .split('-')
      .map(Number);
    const [endYear, endMonth, endDay] = this.planDetails.end_date
      .split('-')
      .map(Number);
    const currentSchoolYear = Number(CURRENT_SCHOOL_YEAR.name.slice(-4));
    let futureYears = 0;
    if (endYear >= currentSchoolYear) {
      if (endMonth <= 6 && endDay <= 30) {
        futureYears = endYear - currentSchoolYear;
      } else {
        futureYears = endYear - currentSchoolYear + 1;
      }
    }
    let adjustedStartYear = startYear;
    if (startMonth >= 7 && startDay >= 1) {
      adjustedStartYear += 1;
    }
    this.planSchoolYearList = getSchoolYearListWithFutures(
      futureYears,
      adjustedStartYear
    );
  }

  // eslint-disable-next-line class-methods-use-this
  checkDeliverableHidden(phase: PhaseStateModel) {
    const deliverableHidden = phase.deliverables.some(
      (deliverable) => deliverable.hidden
    );
    const actionItemHidden = phase.deliverables.some((deliverable) =>
      this.checkActionItemHidden(deliverable)
    );

    if (deliverableHidden) {
      if (actionItemHidden) {
        return `There are additional ${this.planDetails.deliverable_displayname}(s) and ${this.planDetails.actionitem_displayname}(s) that do not match the chosen filter`;
      }
      return `There are additional ${this.planDetails.deliverable_displayname}(s) that do not match the chosen filter`;
    }

    return '';
  }

  // eslint-disable-next-line class-methods-use-this
  checkActionItemHidden(deliverable: DeliverableStateModel) {
    return deliverable.actionItems.some((actionItem) => actionItem.hidden)
      ? `There are additional ${this.planDetails.actionitem_displayname}(s) that do not match the chosen filter`
      : '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.unsubscribe();
  }
}
